import { useContext, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { Box, Grid } from '@mui/material'
import {
  GRID_TREE_DATA_GROUPING_FIELD,
  GridApiPro,
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
  GridRenderCellParams,
  GridRowsProp,
  useGridApiRef,
} from '@mui/x-data-grid-pro'

import { getFolderParents } from '../../../../utils/helpers'
import useFolderTheme from '../../../../utils/hooks/useFolderTheme'
import useSharedStyles from '../../../../utils/shared.styles'
import AccountsContext from '../../context'
import { DEACTIVATED_URL_PARAM } from '../search'

import Address from './address'
import CountChip from './count-chip'
import FolderGrid from './folders-grid'
import useStyles from './index.styles'
import QuickActionsMenu from './quick-actions-menu'
import AccountStatusChip from './status-chip'

interface IFolderRow {
  id: number
  hierarchy: string[]
  name: string
  status: string
  alerts: number
  devices: number
  contacts: number
  hasChildren: boolean
}

const TabPanelFolders = () => {
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const { folder, folderTree, folderRoots, isParticipantView, searchText, isLoading } = useContext(AccountsContext)
  const { folderTheme } = useFolderTheme(folder?.id)
  const showStoreLink = folderTheme?.sensorStoreLink === true || folderTheme?.sensorStoreLink === 'true'
  const [searchParams] = useSearchParams()
  const hideDeactivated = searchParams.get(DEACTIVATED_URL_PARAM) === 'true'

  const shouldShowParticipantView = isParticipantView || folder?.type === 'Participant'

  const gridRef = useGridApiRef() as React.RefObject<GridApiPro>
  const filterModel = useMemo<GridFilterModel>(
    () => ({
      items: [
        {
          id: 1,
          field: GRID_TREE_DATA_GROUPING_FIELD,
          operator: 'contains',
          value: searchText,
        },
        {
          id: 2,
          field: 'status',
          operator: 'isAnyOf',
          value: hideDeactivated ? ['Active', 'Not Activated'] : [],
        },
      ],
      logicOperator: GridLogicOperator.And,
    }),
    [hideDeactivated, searchText]
  )

  const columns = useMemo(() => {
    const participantColumns: GridColDef[] = [
      {
        field: 'address',
        headerName: 'Address',
        sortable: false,
        flex: 3,
        renderCell: (params: GridRenderCellParams<IFolderRow>) => (
          <Grid sx={sharedClasses.fullWidth}>
            <Address folderId={params.row.id} />
          </Grid>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        headerClassName: 'rightAlignedColumnHeader',
        align: 'right',
        minWidth: 100,
        flex: 1,
        renderCell: (params: GridRenderCellParams<IFolderRow>) => (
          <AccountStatusChip accountStatus={params.value} dataTestId={`accountStatus-${params.row.id}`} />
        ),
      },
      {
        field: 'alerts',
        headerName: 'Alerts',
        headerClassName: 'centeredColumnHeader',
        align: 'center',
        minWidth: 75,
        flex: 1,
        renderCell: (params: GridRenderCellParams<IFolderRow>) => (
          <CountChip count={params.value} countType="alertsCount" accountStatus={params.row.status} dataTestId={`alertsCount-${params.row.id}`} />
        ),
      },
      {
        field: 'devices',
        headerName: 'Devices',
        headerClassName: 'centeredColumnHeader',
        align: 'center',
        minWidth: 100,
        flex: 1,
        renderCell: (params: GridRenderCellParams<IFolderRow>) => (
          <CountChip count={params.value} countType="devicesCount" accountStatus={params.row.status} dataTestId={`devicesCount-${params.row.id}`} />
        ),
      },
      {
        field: 'contacts',
        headerName: 'Contacts',
        headerClassName: 'centeredColumnHeader',
        align: 'center',
        minWidth: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams<IFolderRow>) => (
          <CountChip count={params.value} countType="contactsCount" accountStatus={params.row.status} dataTestId={`contactsCount-${params.row.id}`} />
        ),
      },
      {
        field: 'options-menu',
        disableColumnMenu: true,
        sortable: false,
        resizable: false,
        width: 20,
        renderHeader: () => null,
        renderCell: (params: GridRenderCellParams<IFolderRow>) => (
          <QuickActionsMenu
            {...{ folderId: params.row.id, showStoreLink, folderName: params.row.name }}
            showOptOutMenu={params.row.status !== 'Inactive'}
            showReactivateMenu={params.row.status === 'Inactive'}
          />
        ),
      },
    ]

    return shouldShowParticipantView ? participantColumns : []
  }, [sharedClasses.fullWidth, shouldShowParticipantView, showStoreLink])

  const rows: GridRowsProp = useMemo(() => {
    if (folderTree && folderRoots.length > 0) {
      const getChildren = (list: number[]) => {
        const newList = new Set<number>()

        const traverse = (node: number) => {
          if (!node) return
          newList.add(node)
          const children = folderTree[node] && folderTree[node].children
          if (children) {
            children.forEach((child) => traverse(child))
          }
        }

        list.forEach((root) => traverse(root))

        return Array.from(newList)
      }

      const rootFolderId = folder && folderTree[folder.id] ? folder.id : folderRoots[0]

      const list = getChildren(folderRoots.length > 1 ? folderRoots : folderTree[rootFolderId] ? (folderTree[rootFolderId].children ?? []) : [])

      return list
        .map((item) => {
          let hierarchy = getFolderParents(item, folderTree)
          if (folderRoots.length === 1) {
            const hierarchyPositionOfRoot = hierarchy.indexOf(rootFolderId)
            hierarchy = hierarchy.slice(hierarchyPositionOfRoot + 1, hierarchy.length)
          }
          // Remove items from hierarchy that are not in folder tree
          hierarchy = hierarchy.filter((h) => folderTree[h])
          return {
            id: item,
            hierarchy: [...hierarchy, item].map((h) => `${folderTree[h].name} - ${h}`),
            name: folderTree[item].name,
            status: folderTree[item].data?.status ?? '',
            alerts: folderTree[item].data?.alertsCount ?? 0,
            devices: folderTree[item].data?.devicesCount ?? 0,
            contacts: folderTree[item].data?.contactsCount ?? 0,
            hasChildren: !!folderTree[item].children?.length,
          }
        })
        .filter((item) => !!item.name) // remove folders without names
    }
    return []
  }, [folder, folderRoots, folderTree])

  return (
    <Box sx={classes.dataGridContainer}>
      <FolderGrid
        gridRef={gridRef}
        rows={rows}
        columns={columns}
        isLoading={isLoading}
        filterModel={filterModel}
        shouldShowParticipantView={shouldShowParticipantView}
        searchText={searchText}
      />
    </Box>
  )
}

export default TabPanelFolders
